import type {
  Asset, AuditPage, Booklet, Change, ColumnMapping, Comment, HistoryEntry, ImportResult, Preflight, Project,
  ProjectSummary, RenderJob, Role, Status, Template, TemplateOptions, TemplateTokens, User, Version,
} from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '';

/** Fired when the session runs out, so the app can show the sign-in screen. */
export const SIGNED_OUT_EVENT = 'wnw:signed-out';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: init?.body instanceof FormData
      ? init?.headers
      : { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }
  if (!res.ok) {
    const err = new Error((data as { error?: string })?.error ?? `${res.status} ${res.statusText}`) as Error & { status?: number; data?: unknown };
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as T;
}

const json = (body: unknown) => JSON.stringify(body);

export const api = {
  // ---- auth ----
  me: () => request<User>('/api/auth/me'),
  login: (username: string, password: string) => request<User>('/api/auth/login', { method: 'POST', body: json({ username, password }) }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  changePassword: (current: string, next: string) => request<void>('/api/auth/password', { method: 'POST', body: json({ current, next }) }),

  // ---- users (admin) ----
  listUsers: () => request<User[]>('/api/users'),
  createUser: (body: { username: string; name: string; role: Role; password: string }) =>
    request<User>('/api/users', { method: 'POST', body: json(body) }),
  updateUser: (id: string, body: Partial<{ name: string; role: Role; disabled: boolean; password: string }>) =>
    request<User>(`/api/users/${id}`, { method: 'PATCH', body: json(body) }),
  deleteUser: (id: string) => request<void>(`/api/users/${id}`, { method: 'DELETE' }),

  // ---- projects ----
  listProjects: () => request<ProjectSummary[]>('/api/projects'),
  getProject: (id: string) => request<Project>(`/api/projects/${id}`),
  createProject: (body: { name: string; seed?: 'garmisch'; templateId?: string }) =>
    request<Project>('/api/projects', { method: 'POST', body: json(body) }),
  updateProject: (id: string, body: { name?: string; content?: Booklet; templateId?: string }) =>
    request<Project>(`/api/projects/${id}`, { method: 'PATCH', body: json(body) }),
  deleteProject: (id: string) => request<void>(`/api/projects/${id}`, { method: 'DELETE' }),
  duplicateProject: (id: string, name: string) =>
    request<Project>(`/api/projects/${id}/duplicate`, { method: 'POST', body: json({ name }) }),
  preflight: (id: string, layout = false) => request<Preflight>(`/api/projects/${id}/preflight${layout ? '?layout=1' : ''}`),
  transition: (id: string, to: Status) =>
    request<Project>(`/api/projects/${id}/transition`, { method: 'POST', body: json({ to }) }),
  history: (id: string) => request<HistoryEntry[]>(`/api/projects/${id}/history`),

  /** Admin-only: everything everyone has changed. */
  audit: (filter: { user?: string; action?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.user) q.set('user', filter.user);
    if (filter.action) q.set('action', filter.action);
    q.set('limit', String(filter.limit ?? 300));
    return request<AuditPage>(`/api/audit?${q}`);
  },

  // ---- comments ----
  listComments: (id: string) => request<Comment[]>(`/api/projects/${id}/comments`),
  addComment: (id: string, body: { body: string; fieldPath?: string | null; page?: number | null }) =>
    request<Comment>(`/api/projects/${id}/comments`, { method: 'POST', body: json(body) }),
  resolveComment: (id: string, commentId: string, resolved: boolean) =>
    request<Comment>(`/api/projects/${id}/comments/${commentId}`, { method: 'PATCH', body: json({ resolved }) }),
  deleteComment: (id: string, commentId: string) =>
    request<void>(`/api/projects/${id}/comments/${commentId}`, { method: 'DELETE' }),

  // ---- imports ----
  importDocx: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<ImportResult>(`/api/projects/${id}/import/docx`, { method: 'POST', body: form });
  },
  importSheet: (id: string, file: File, opts: { sheet?: string; mapping?: ColumnMapping; headerRow?: number } = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (opts.sheet) form.append('sheet', opts.sheet);
    if (opts.mapping) form.append('mapping', JSON.stringify(opts.mapping));
    if (opts.headerRow != null) form.append('headerRow', String(opts.headerRow));
    return request<ImportResult>(`/api/projects/${id}/import/sheet`, { method: 'POST', body: form });
  },
  importSheetUrl: (id: string, url: string, opts: { sheet?: string; mapping?: ColumnMapping; headerRow?: number } = {}) =>
    request<ImportResult>(`/api/projects/${id}/import/sheet-url`, { method: 'POST', body: json({ url, ...opts }) }),
  applyImport: (id: string, token: string, choices: Record<string, 'current' | 'incoming'>) =>
    request<Project>(`/api/projects/${id}/import/apply`, { method: 'POST', body: json({ token, choices }) }),

  // ---- assets ----
  listAssets: (id: string) => request<Asset[]>(`/api/projects/${id}/assets`),
  uploadAsset: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Asset>(`/api/projects/${id}/assets`, { method: 'POST', body: form });
  },
  deleteAsset: (id: string, assetId: string, force = false) =>
    request<void>(`/api/projects/${id}/assets/${assetId}${force ? '?force=true' : ''}`, { method: 'DELETE' }),

  /** Blank Word / Excel forms for the team to fill in offline. */
  importFormUrl: (id: string, kind: 'docx' | 'xlsx') =>
    `${BASE}/api/projects/${id}/import/form.${kind}`,

  // ---- render, publish, versions ----
  previewUrl: (id: string, opts: { edit?: boolean; bleed?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.edit) q.set('edit', '1');
    if (opts.bleed) q.set('bleed', '1');
    const qs = q.toString();
    return `${BASE}/api/projects/${id}/preview.html${qs ? `?${qs}` : ''}`;
  },
  startRender: (id: string, preset: 'web' | 'print') =>
    request<RenderJob>(`/api/projects/${id}/renders`, { method: 'POST', body: json({ preset }) }),
  renderStatus: (jobId: string) => request<RenderJob>(`/api/renders/${jobId}`),
  publish: (id: string, label: string) =>
    request<Version>(`/api/projects/${id}/publish`, { method: 'POST', body: json({ label }) }),
  listVersions: (id: string) => request<Version[]>(`/api/projects/${id}/versions`),
  versionDiff: (id: string, versionId: string) =>
    request<{ version: number; changes: Change[] }>(`/api/projects/${id}/versions/${versionId}/diff`),
  restoreVersion: (id: string, versionId: string) =>
    request<Project>(`/api/projects/${id}/versions/${versionId}/restore`, { method: 'POST' }),
  shareVersion: (id: string, versionId: string) =>
    request<{ token: string; path: string }>(`/api/projects/${id}/versions/${versionId}/share`, { method: 'POST' }),
  unshareVersion: (id: string, versionId: string) =>
    request<void>(`/api/projects/${id}/versions/${versionId}/share`, { method: 'DELETE' }),
  routeMapInfo: (id: string, refresh = false) =>
    request<{ provider: string; distanceKm: number | null; warnings: string[]; missing: string[] }>(`/api/projects/${id}/route-map?info=1${refresh ? '&refresh=1' : ''}`),

  // ---- templates ----
  listTemplates: () => request<Template[]>('/api/templates'),
  getTemplate: (id: string) => request<Template>(`/api/templates/${id}`),
  templateOptions: () => request<TemplateOptions>('/api/templates/meta/options'),
  duplicateTemplate: (from: string, name: string) =>
    request<Template>('/api/templates', { method: 'POST', body: json({ from, name }) }),
  updateTemplate: (id: string, body: Partial<{ name: string; tokens: TemplateTokens; pageOrder: string[] }>) =>
    request<Template>(`/api/templates/${id}`, { method: 'PATCH', body: json(body) }),
  deleteTemplate: (id: string) => request<void>(`/api/templates/${id}`, { method: 'DELETE' }),
  templatePreviewUrl: (id: string, draft?: { tokens: TemplateTokens; pageOrder: string[] }) => {
    if (!draft) return `${BASE}/api/templates/${id}/preview.html`;
    const q = new URLSearchParams({ tokens: JSON.stringify(draft.tokens), order: JSON.stringify(draft.pageOrder) });
    return `${BASE}/api/templates/${id}/preview.html?${q}`;
  },
};

/** Poll a render job until it finishes (or the timeout hits). */
export async function waitForRender(jobId: string, onTick?: (job: RenderJob) => void): Promise<RenderJob> {
  const deadline = Date.now() + 120_000;
  for (;;) {
    const job = await api.renderStatus(jobId);
    onTick?.(job);
    if (job.status === 'done' || job.status === 'failed') return job;
    if (Date.now() > deadline) throw new Error('The PDF is taking too long — try again in a minute');
    await new Promise((r) => setTimeout(r, 1200));
  }
}
